import "./support.css";
import API from "../../../API";
import Swal from "sweetalert2";
import React, { useState } from "react";
import { useTranslation } from 'react-i18next';
import {
  FaHeadset, FaUser, FaEnvelope, FaTag, FaPencilAlt, FaPaperPlane
} from "react-icons/fa";

const SupportTypes = {
  OTHER: "other",
  ISSUE: "issue",
  FEEDBACK: "feedback",
  RESERVATION: "reservation",
  PAYMENT: "payment",
  FLIGHT_INFO: "flight_info",
  ACCOMMODATION: "accommodation",
  VISA_ASSISTANCE: "visa_assistance",
  TRAVEL_INSURANCE: "travel_insurance",
  CANCELLATION: "cancellation",
  SPECIAL_REQUEST: "special_request",
  LOST_PROPERTY: "lost_property",
  ITINERARY_MODIFICATION: "itinerary_modification",
  EMERGENCY_ASSISTANCE: "emergency_assistance",
  GROUP_BOOKING: "group_booking",
  LOYALTY_PROGRAM: "loyalty_program",
  COMPLAINT: "complaint",
  TRAVEL_RESTRICTIONS: "travel_restrictions",
};

export default function Support({ userdata, onTicketCreated }) {
  const { t } = useTranslation(); // Initialize the translation hook
  const [type, setType] = useState("other");
  const [otherType, setOtherType] = useState("");
  const [description, setDescription] = useState("");

  const email = localStorage.getItem("email");
  const fullname = localStorage.getItem("fullname");
  const userId = localStorage.getItem("userId");
  const protoken = localStorage.getItem("protoken");


  const supportOptions = [
    { value: SupportTypes.OTHER, label: t("support.support_types.other") },
    { value: SupportTypes.ISSUE, label: t("support.support_types.issue") },
    { value: SupportTypes.FEEDBACK, label: t("support.support_types.feedback") },
    { value: SupportTypes.RESERVATION, label: t("support.support_types.reservation") },
    { value: SupportTypes.PAYMENT, label: t("support.support_types.payment") },
    { value: SupportTypes.FLIGHT_INFO, label: t("support.support_types.flight_info") },
    { value: SupportTypes.ACCOMMODATION, label: t("support.support_types.accommodation") },
    { value: SupportTypes.VISA_ASSISTANCE, label: t("support.support_types.visa_assistance") },
    { value: SupportTypes.TRAVEL_INSURANCE, label: t("support.support_types.travel_insurance") },
    { value: SupportTypes.CANCELLATION, label: t("support.support_types.cancellation") },
    { value: SupportTypes.SPECIAL_REQUEST, label: t("support.support_types.special_request") },
    { value: SupportTypes.LOST_PROPERTY, label: t("support.support_types.lost_property") },
    { value: SupportTypes.ITINERARY_MODIFICATION, label: t("support.support_types.itinerary_modification") },
    { value: SupportTypes.EMERGENCY_ASSISTANCE, label: t("support.support_types.emergency_assistance") },
    { value: SupportTypes.GROUP_BOOKING, label: t("support.support_types.group_booking") },
    { value: SupportTypes.LOYALTY_PROGRAM, label: t("support.support_types.loyalty_program") },
    { value: SupportTypes.COMPLAINT, label: t("support.support_types.complaint") },
    { value: SupportTypes.TRAVEL_RESTRICTIONS, label: t("support.support_types.travel_restrictions") },
  ];

  const handleTypeChange = (e) => {
    setType(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent default form submission

    // If user chose "Other", a custom subject is required
    if (type === "other" && !otherType.trim()) {
      Swal.fire({
        icon: "warning",
        title: t("support.other_required_title") || "Subject required",
        text: t("support.other_required_text") || "Please enter a subject for your request.",
        confirmButtonText: "OK",
      });
      return;
    }

    // Description is also required
    if (!description.trim()) {
      Swal.fire({
        icon: "warning",
        title: t("support.description_required_title") || "Description required",
        text: t("support.description_required_text") || "Please describe your request.",
        confirmButtonText: "OK",
      });
      return;
    }

    // Constructing the request body
    const data = {
      subject: type === "other" ? otherType.trim() : type,
      typeRequest: type,
      autre: type === "other" ? otherType.trim() : undefined,
      message: description.trim(),
      userId: userId,
    };

    try {
      const config = {
        headers: {
          Authorization: `Bearer ${protoken}`,
        },
      };

      const response = await API.post("/support-tickets", data, config);

      // SweetAlert success message
      Swal.fire({
        icon: "success",
        title: t("support.success_title"),
        text: t("support.success_message"),
        confirmButtonText: "OK",
      }).then(() => {
        // Switch to the My Tickets tab
        if (onTicketCreated) {
          onTicketCreated();
        }
      });
    } catch (error) {
      console.error("Error submitting ticket:", error);
      Swal.fire({
        icon: "error",
        title: t("support.error_title"),
        text: t("support.error_message"),
        confirmButtonText: "OK",
      });
    }
  };

  return (
    <form className="sup-form" onSubmit={handleSubmit}>
      <header className="sup-head">
        <span className="sup-eyebrow">
          <FaHeadset /> {t("support.title")}
        </span>
        <p className="sup-sub">
          Let us know how we can help — we usually reply within a few hours.
        </p>
      </header>

      <div className="sup-grid">
        <div className="sup-field">
          <label className="sup-label">
            <FaUser className="sup-label-ico" />
            {t("support.fullname_label")}
          </label>
          <input
            type="text"
            className="sup-input is-readonly"
            placeholder={t("support.fullname_placeholder")}
            value={fullname || ""}
            readOnly
          />
        </div>
        <div className="sup-field">
          <label className="sup-label">
            <FaEnvelope className="sup-label-ico" />
            {t("support.email_label")}
          </label>
          <input
            type="email"
            className="sup-input is-readonly"
            placeholder={t("support.email_placeholder")}
            value={email || ""}
            readOnly
          />
        </div>

        <div className="sup-field">
          <label className="sup-label">
            <FaTag className="sup-label-ico" />
            {t("support.support_type_label")}
          </label>
          <select
            className="sup-select"
            value={type || ""}
            onChange={handleTypeChange}
          >
            <option value="" disabled>
              {t("support.support_type_placeholder")}
            </option>
            {supportOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        <div className={`sup-field ${type !== "other" ? "is-disabled" : ""}`}>
          <label className="sup-label">
            <FaPencilAlt className="sup-label-ico" />
            {t("support.other_label")}
            {type === "other" && <span className="sup-required">*</span>}
          </label>
          <input
            type="text"
            className="sup-input"
            placeholder={t("support.other_placeholder")}
            value={otherType}
            onChange={(e) => setOtherType(e.target.value)}
            disabled={type !== "other"}
            required={type === "other"}
          />
        </div>

        <div className="sup-field sup-field--full">
          <label className="sup-label">
            <FaPencilAlt className="sup-label-ico" />
            {t("support.description_label")}
          </label>
          <textarea
            className="sup-textarea"
            placeholder={t("support.description_placeholder")}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={6}
          />
        </div>
      </div>

      <div className="sup-actions">
        <button type="submit" className="sup-submit">
          <FaPaperPlane /> {t("support.send_button")}
        </button>
      </div>
    </form>
  );
}